import { Injectable } from '@angular/core';

import { ModalService, ModalType } from '@ipreo/ngx-sprinkles';
import { Observable, combineLatest } from 'rxjs';
import { map, take, filter, switchMap } from 'rxjs/operators';
import {
  ExportServiceOption,
  ExportModalAction,
  ExportAction,
  CancelAction,
  ExportModalOption
} from '../types/export-modal.types';
import { ExportModalComponent } from '../components/export-modal/export-modal.component';
import { ExportFacade } from '../facades/export.facade';
import { ExcelWorkSheet } from '../utils/export/excel/worksheet';
import {
  PendoDealMonitorExport,
  PendoDealShowExport,
  PendoBidCompExport,
  PendoTrackingActionTypes
} from '../state/pendo/actions';
import { Store } from '@ngrx/store';
import { generateExcelWorkBook } from '../utils/export/excel/workbook';

@Injectable()
export class ExportModalService {
  constructor(
    protected modalService: ModalService,
    private exportFacade: ExportFacade,
    private store: Store
  ) {}

  public handleExport(
    pendoAction:
      | PendoTrackingActionTypes.DealMonitorExport
      | PendoTrackingActionTypes.DealShowExport
      | PendoTrackingActionTypes.BidCompExport,
    dealId?: string
  ) {
    this.exportFacade.exportOptions$
      .pipe(
        take(1),
        switchMap((options: ExportServiceOption[]) =>
          this.openExportModal(options)
        ),
        filter(
          (exportAction) => exportAction.action !== ExportModalAction.cancel
        ),
        map((e: ExportAction) =>
          e.options.filter((option) => option.isChecked)
        ),
        switchMap((selectedOptions: ExportModalOption[]) => {
          return combineLatest(
            this.exportFacade.exportWorksheets(selectedOptions),
            this.exportFacade.getFilename()
          );
        })
      )
      .subscribe(([worksheets, filename]: [ExcelWorkSheet[], string]) => {
        generateExcelWorkBook(filename, worksheets);
        let action;
        switch (pendoAction) {
          case PendoTrackingActionTypes.DealMonitorExport:
            action = new PendoDealMonitorExport();
            break;

          case PendoTrackingActionTypes.DealShowExport:
            action = new PendoDealShowExport(dealId);
            break;

          case PendoTrackingActionTypes.BidCompExport:
            action = new PendoBidCompExport();
            break;

          default:
            action = undefined;
            break;
        }
        if (action) {
          this.store.dispatch(action);
        }
      });
  }

  private openExportModal(
    options: ExportServiceOption[]
  ): Observable<ExportAction | CancelAction> {
    return this.modalService.create({
      showOverlay: true,
      closeOnOverlayClick: true,
      title: 'Export Options',
      context: { options: this.mapToExportModalOptions(options) },
      type: ModalType.View,
      view: ExportModalComponent,
      width: 300
    }).instance.response;
  }

  public mapToExportModalOptions(
    options: ExportServiceOption[]
  ): ExportModalOption[] {
    return options.map((option) => ({
      ...option,
      isChecked: option.isCheckedByDefault,
      configOptions: option.configOptions.map((configOption) => ({
        ...configOption,
        isChecked: option.isCheckedByDefault && configOption.isCheckedByDefault
      }))
    }));
  }
}
